import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { statusApi } from '@/api/status';
import { tasksApi } from '@/api/tasks';
import { playNewTaskSound } from '@/utils/notificationSound';
import { requestNotificationPermission, showBrowserNotification } from '@/utils/browserNotification';
import type { DriverStatusType, TaskListItem } from '@/types';
import { BottomNav } from '@/components/BottomNav';

const POLL_INTERVAL = 30_000;

export function HomePage() {
  const navigate = useNavigate();
  const [driverStatus, setDriverStatus] = useState<DriverStatusType>('OFFLINE');
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevTaskIdsRef = useRef<Set<string>>(new Set());

  const fetchStatus = useCallback(async () => {
    try {
      const res = await statusApi.getStatus();
      if (res.data) setDriverStatus(res.data.status);
    } catch {
      // ignore
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await tasksApi.getTasks();
      if (res.data) {
        const newTasks = res.data.tasks;
        // Check for newly assigned tasks that weren't in previous poll
        const prevIds = prevTaskIdsRef.current;
        const newAssigned = newTasks.filter(
          (t) => (t.status === 'DRIVER_ASSIGNED' || t.status === 'CREATED') && !prevIds.has(t.id)
        );
        if (newAssigned.length > 0 && prevIds.size > 0) {
          playNewTaskSound();
          showBrowserNotification(
            'New Delivery Task',
            `${newAssigned.length} new task${newAssigned.length > 1 ? 's' : ''} — ${newAssigned[0].vendorName}`
          );
        }
        prevTaskIdsRef.current = new Set(newTasks.map((t) => t.id));
        setTasks(newTasks);
      }
    } catch {
      // ignore
    }
  }, []);

  // Request browser notification permission
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Initial load
  useEffect(() => {
    Promise.all([fetchStatus(), fetchTasks()]).finally(() => setLoading(false));
  }, [fetchStatus, fetchTasks]);

  // Refetch when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchTasks();
        fetchStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchTasks, fetchStatus]);

  // Polling when ONLINE
  useEffect(() => {
    if (driverStatus === 'ONLINE') {
      intervalRef.current = setInterval(fetchTasks, POLL_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [driverStatus, fetchTasks]);

  const toggleStatus = async () => {
    setToggling(true);
    const next: DriverStatusType = driverStatus === 'ONLINE' ? 'OFFLINE' : 'ONLINE';
    try {
      // Request notification permission on user gesture (required by mobile browsers)
      if (next === 'ONLINE') requestNotificationPermission();
      await statusApi.setStatus(next);
      setDriverStatus(next);
      if (next === 'ONLINE') fetchTasks();
    } catch {
      // ignore
    } finally {
      setToggling(false);
    }
  };

  const isOnline = driverStatus === 'ONLINE';
  const activeTasks = tasks.filter(
    (t) => t.status !== 'DELIVERED' && t.status !== 'CANCELLED' && t.status !== 'FAILED' && t.status !== 'CREATED'
  );

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '100dvh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 72 }}>
      <div className="container">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingTop: 8 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>MacroRides</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Rider Dashboard</p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              borderRadius: 20,
              background: isOnline ? 'var(--success-light)' : 'var(--danger-light)',
              fontSize: 13,
              fontWeight: 600,
              color: isOnline ? '#166534' : '#991b1b',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isOnline ? 'var(--success)' : 'var(--danger)',
              }}
            />
            {isOnline ? 'Online' : 'Offline'}
          </div>
        </div>

        {/* Toggle */}
        <div className="card" style={{ textAlign: 'center', padding: 32, marginBottom: 24 }}>
          <button
            onClick={toggleStatus}
            disabled={toggling}
            style={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              border: `4px solid ${isOnline ? 'var(--success)' : 'var(--border)'}`,
              background: isOnline ? 'var(--success)' : 'var(--bg)',
              color: isOnline ? '#fff' : 'var(--text)',
              fontSize: 18,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: isOnline ? '0 0 30px rgba(22,163,74,0.3)' : 'var(--shadow)',
            }}
          >
            {toggling ? '...' : isOnline ? 'GO\nOFFLINE' : 'GO\nONLINE'}
          </button>
          <p style={{ marginTop: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
            {isOnline
              ? 'You are receiving delivery tasks'
              : 'Go online to receive tasks'}
          </p>
        </div>

        {/* Active tasks summary */}
        {isOnline && activeTasks.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Active Tasks</h2>
            {activeTasks.slice(0, 3).map((task) => (
              <div
                key={task.id}
                className="card"
                style={{ marginBottom: 8, cursor: 'pointer' }}
                onClick={() => navigate(`/tasks/${task.type.toLowerCase()}/${task.id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 15 }}>{task.vendorName}</p>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{task.dropArea || task.dropAddress}</p>
                  </div>
                  <span className={`badge badge-${task.status === 'DRIVER_ASSIGNED' ? 'assigned' : 'picked'}`}>
                    {task.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            ))}
            {activeTasks.length > 3 && (
              <button
                className="btn btn-outline btn-full"
                style={{ marginTop: 8, fontSize: 14 }}
                onClick={() => navigate('/tasks')}
              >
                View all tasks
              </button>
            )}
          </div>
        )}

        {/* New tasks (assigned) */}
        {isOnline && tasks.filter((t) => t.status === 'CREATED').length > 0 && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>New Tasks Available</h2>
            {tasks
              .filter((t) => t.status === 'CREATED')
              .slice(0, 3)
              .map((task) => (
                <div
                  key={task.id}
                  className="card"
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => navigate(`/tasks/${task.type.toLowerCase()}/${task.id}`)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 15 }}>{task.vendorName}</p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{task.dropArea || task.dropAddress}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className="badge badge-created">NEW</span>
                      {task.paymentType === 'COD' && (
                        <p style={{ fontSize: 12, color: 'var(--warning)', fontWeight: 600, marginTop: 4 }}>
                          COD ₹{task.codAmount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {isOnline && tasks.length === 0 && (
          <div className="empty-state">
            <p style={{ fontSize: 40, marginBottom: 8 }}>📦</p>
            <p>No tasks available right now</p>
            <p style={{ fontSize: 13 }}>New tasks will appear here</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
